// src/pages/services/DataAnalytics.jsx - Premium Analytics Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, BarChart3, PieChart, TrendingUp, Database, Zap, Target, ExternalLink } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function DataAnalytics() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Business Intelligence',
      description: 'Transform data into actionable business insights',
      features: ['Custom Dashboards', 'KPI Tracking', 'Automated Reports', 'Real-time Analytics']
    },
    {
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Predictive Analytics',
      description: 'Forecast trends and make data-driven decisions',
      features: ['Sales Forecasting', 'Customer Churn', 'Demand Planning', 'Risk Analysis']
    },
    {
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Data Engineering',
      description: 'Build robust data pipelines and warehouses',
      features: ['ETL Pipelines', 'Data Warehousing', 'Data Lake', 'API Integration']
    },
    {
      icon: <PieChart className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Data Visualization',
      description: 'Create compelling visual stories from data',
      features: ['Interactive Dashboards', 'Custom Charts', 'Reporting', 'Data Stories']
    }
  ];

  const tools = [
    { name: 'Power BI', icon: '📊', category: 'Visualization' },
    { name: 'Tableau', icon: '📈', category: 'Visualization' },
    { name: 'Python', icon: '🐍', category: 'Analysis' },
    { name: 'SQL', icon: '🗄️', category: 'Database' },
    { name: 'Apache Spark', icon: '⚡', category: 'Big Data' },
    { name: 'Snowflake', icon: '❄️', category: 'Data Warehouse' }
  ];

  const analyticsTypes = [
    'Descriptive Analytics', 'Diagnostic Analytics', 'Predictive Analytics',
    'Prescriptive Analytics', 'Real-time Analytics', 'Customer Analytics'
  ];

  const benefits = [
    { title: 'Better Decisions', value: '5x Faster', description: 'Data-driven decision making' },
    { title: 'Cost Reduction', value: 'Up to 40%', description: 'Operational efficiency' },
    { title: 'Revenue Growth', value: '25%+', description: 'Increased profitability' },
    { title: 'Customer Insight', value: '360° View', description: 'Complete understanding' }
  ];

  const pricingPlans = [
    {
      name: 'Analytics Dashboard',
      price: 19999,
      monthlySupport: 499,
      color: 'green',
      badge: null,
      features: [
        'Custom Dashboard Development',
        'Data Integration & ETL',
        'Basic Analytics & Reports',
        '2 Months Support Included',
        'User Training',
        'Documentation'
      ]
    },
    {
      name: 'Advanced Analytics',
      price: 59999,
      monthlySupport: 1999,
      color: 'teal',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Predictive & ML Models',
        'Data Warehouse Setup',
        'Custom Analytics Platform',
        '4 Months Support Included',
        'Advanced Visualizations',
        'API Development',
        'Performance Optimization'
      ]
    },
    {
      name: 'Enterprise BI',
      price: null,
      monthlySupport: 4999,
      color: 'emerald',
      badge: null,
      features: [
        'Full Business Intelligence Platform',
        'Dedicated Data Team',
        '24/7 Monitoring & Support',
        'Advanced ML Implementations',
        'Real-time Data Processing',
        'Continuous Optimization',
        'Strategic Consulting'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-green-600 to-teal-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Data Analytics Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-green-100 mb-6 sm:mb-8 leading-relaxed">
                Turn your data into competitive advantage. Expert analytics, visualization, and predictive modeling to drive business growth.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-green-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Analytics Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Analytics Projects
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop" alt="Data Analytics" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-2xl sm:text-4xl font-black text-green-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{benefit.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{benefit.title}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{benefit.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Analytics Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive data analytics solutions from collection to insights
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center text-green-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* Data Transformation Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-green-600/10 via-teal-600/10 to-emerald-600/10 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-12 shadow-2xl border border-green-200/50 dark:border-green-800/50">
            <Database className="w-16 h-16 sm:w-24 sm:h-24 text-green-600 mx-auto mb-4 sm:mb-8 opacity-75 animate-pulse" />
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 via-teal-600 to-emerald-600 bg-clip-text text-transparent mb-4 sm:mb-6 text-center">
              Transform Data Into Business Insights
            </h2>
            <p className="text-base sm:text-xl text-gray-700 dark:text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed text-center px-2">
              We design and implement analytics solutions tailored to your specific business needs. 
              From customer segmentation to predictive maintenance and real-time dashboards - 
              <strong className="font-semibold text-green-600 dark:text-green-400"> we build custom analytics systems to drive informed decisions</strong>. 
              Let us help you unlock the value in your data.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-12">
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 rounded-2xl border border-green-200/50 hover:shadow-2xl transition-all duration-300">
                <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">Retail Analytics</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-green-600">Customer Segmentation</div>
                  <div className="text-base sm:text-lg text-teal-600">Inventory Optimization</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 rounded-2xl border border-green-200/50 hover:shadow-2xl transition-all duration-300">
                <Zap className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">Manufacturing</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-green-600">Predictive Maintenance</div>
                  <div className="text-base sm:text-lg text-teal-600">Quality Control Analytics</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 rounded-2xl border border-green-200/50 hover:shadow-2xl transition-all duration-300">
                <TrendingUp className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">E-commerce</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-green-600">Sales Analytics</div>
                  <div className="text-base sm:text-lg text-teal-600">Customer Behavior Analysis</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:gap-6">
              <Link to="/contact" className="px-6 sm:px-12 py-3 sm:py-5 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300 text-center tracking-wide">
                🚀 Start Your Project
              </Link>
              <Link to="/portfolio" className="px-6 sm:px-12 py-3 sm:py-5 border-2 sm:border-4 border-green-600 text-green-600 dark:text-green-400 rounded-2xl font-black text-base sm:text-xl hover:bg-green-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                View Our Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Types */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Types of Analytics We Deliver
          </h2>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {analyticsTypes.map((type, index) => (
              <div key={index} className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-base shadow-lg hover:scale-105 transition-transform cursor-default">
                {type}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Analytics Tools & Technologies
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {tools.map((tool, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{tool.icon}</div>
                <h3 className="font-bold text-xs sm:text-base text-gray-900 dark:text-white">{tool.name}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{tool.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Our Analytics Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Data Collection', desc: 'Gather relevant data' },
              { step: '2', title: 'Data Cleaning', desc: 'Prepare & validate' },
              { step: '3', title: 'Analysis', desc: 'Extract insights' },
              { step: '4', title: 'Visualization', desc: 'Create dashboards' },
              { step: '5', title: 'Action', desc: 'Implement insights' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-green-600 to-teal-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Analytics Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for analytics solutions. From dashboards to enterprise BI platforms.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                green: { bg: 'from-green-600 to-teal-600', light: 'from-green-50 to-teal-50', hover: 'hover:border-green-200' },
                teal: { bg: 'from-teal-600 to-cyan-600', light: 'from-teal-50 to-cyan-50', hover: 'hover:border-teal-200' },
                emerald: { bg: 'from-emerald-600 to-green-600', light: 'from-emerald-50 to-green-50', hover: 'hover:border-emerald-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-teal-500 dark:border-teal-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 dark:from-teal-600/20 dark:to-cyan-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Analytics Dashboard' && 'Perfect to start insights journey'}
                    {plan.name === 'Advanced Analytics' && 'Best for predictive modeling'}
                    {plan.name === 'Enterprise BI' && 'Complete analytics platform'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'green' ? '#16a34a, #0d9488' : plan.color === 'teal' ? '#0d9488, #06b6d4' : '#059669, #10b981'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'green' ? '#16a34a, #0d9488' : plan.color === 'teal' ? '#0d9488, #06b6d4' : '#059669, #10b981'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'green' ? '#16a34a, #0d9488' : plan.color === 'teal' ? '#0d9488, #06b6d4' : '#059669, #10b981'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'green' ? '#dcfce7' : plan.color === 'teal' ? '#ccfbf1' : '#d1fae5'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'green' ? '#16a34a' : plan.color === 'teal' ? '#0d9488' : '#059669'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Enterprise BI' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '📊', text: 'Data Integration' },
                { icon: '📈', text: 'Performance Analytics' },
                { icon: '👥', text: 'User Training' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-2xl border border-green-200/50 dark:border-green-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Data Analytics FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does an analytics project take?',
                  a: 'Dashboard projects: 2-4 weeks. Advanced analytics: 2-3 months. Enterprise BI: Custom timeline. We provide detailed timelines during discovery phase.'
                },
                {
                  q: 'What data sources can you integrate?',
                  a: 'We integrate data from databases, cloud storage, APIs, ERP/CRM systems, web analytics, and more. We handle complex ETL pipelines and data transformation.'
                },
                {
                  q: 'Do you provide staff training?',
                  a: 'Yes! All plans include comprehensive training on dashboards, tools, and data interpretation. We ensure your team can independently use the analytics platform.'
                },
                {
                  q: 'Can you help with data governance?',
                  a: 'Absolutely! We implement data quality standards, security protocols, access controls, and compliance frameworks to ensure reliable analytics.'
                },
                {
                  q: 'What if we need to scale the solution?',
                  a: 'Our solutions are built for scale. We can expand data sources, add new analytics, handle increased volume, and evolve with your business needs.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-green-600 to-teal-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Target className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Unlock Your Data's Potential?
          </h2>
          <p className="text-base sm:text-xl text-green-100 mb-6 sm:mb-8 px-2">
            Transform data into insights and insights into action. Let's start your analytics journey today.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-green-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>📊 Start Your Analytics Project</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="data-analytics" />
      </div>
    </div>
  );
}